import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Button } from './ui/button';
import { BookOpen, Users, LogOut, Plus, Search, FileText } from 'lucide-react';
import ClientsList from './ClientsList';
import ClientDetail from './ClientDetail';
import AddClient from './AddClient';
import AddUser from './AddUser';
import RecentActivity from './RecentActivity';
import MyProfile from './MyProfile';
import UsersList from './UsersList';
import InactiveClientsList from './InactiveClientsList';


const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);

  // derive current user from prop or localStorage safely
  const currentUser = user || (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  useEffect(() => {
    fetchStats();
    
    // If OM role, redirect to users page (they shouldn't see dashboard)
    if (currentUser?.role === 'om' && location.pathname === '/') {
      navigate('/users');
    }
  }, [currentUser, location.pathname, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <header className="nav-beast">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <img 
                src="https://beastmodelegacy.com/wp-content/uploads/2021/07/LogoBEASTMODE.LEGACY.NEWgoldoval-1.png" 
                alt="BEASTMODE Legacy Logo" 
                className="h-28"
              />
              <div>
                <h1 className="text-2xl font-bold title-gold sr-only" data-testid="dashboard-header">BEASTMODE Legacy</h1>
                {currentUser && (
                  <p className="text-base text-slate-300">
                    Logged in as <span className="font-semibold text-slate-100" data-testid="user-name">{currentUser.full_name}</span> •{' '}
                    <span className="capitalize font-semibold title-gold" data-testid="user-role">{currentUser.role}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/profile')}
                className="btn-gold-outline flex items-center gap-2"
                data-testid="profile-btn"
              >
                <Users className="h-4 w-4" />
                My Profile
              </Button>
              <Button
                onClick={onLogout}
                className="btn-red flex items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar - Hide from OM */}
      {stats && currentUser?.role !== 'om' && (
        <div className="nav-beast">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 bg-black/40 rounded-lg border border-slate-700">
                <div className="p-2 bg-slate-800 rounded-lg">
                  <Users className="h-5 w-5 title-gold" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Managers</p>
                  <p className="text-2xl font-bold text-slate-100" data-testid="total-clients">{stats.total_clients}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-black/40 rounded-lg border border-slate-700">
                <div className="p-2 bg-slate-800 rounded-lg">
                  <FileText className="h-5 w-5 title-gold" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Total Notes</p>
                  <p className="text-2xl font-bold text-slate-100" data-testid="total-notes">{stats.total_notes}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-black/40 rounded-lg border border-slate-700">
                <div className="p-2 bg-slate-800 rounded-lg">
                  <BookOpen className="h-5 w-5 title-gold" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Most Documented Manager</p>
                  <p className="text-lg font-bold text-slate-100" data-testid="top-manager">
                    {stats.top_managers && stats.top_managers.length > 0
                      ? `${stats.top_managers[0].name} (${stats.top_managers[0].note_count} notes)`
                      : 'No data yet'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="nav-beast">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-4 py-3">
            {/* Show Managers button only for admin, leader, and booker */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'leader' || currentUser?.role === 'booker') && (
              <Button
                onClick={() => navigate('/')}
                className={isActive('/') ? 'btn-gold' : 'btn-gold-outline'}
                data-testid="nav-clients-btn"
              >
                <Users className="h-4 w-4 mr-2" />
                Managers
              </Button>
            )}
            
            {/* Admin-only buttons */}
            {(currentUser?.role === 'admin') && (
              <>
                <Button
                  onClick={() => navigate('/add-client')}
                  className={isActive('/add-client') ? 'btn-gold' : 'btn-gold-outline'}
                  data-testid="nav-add-client-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Manager
                </Button>
                <Button
                  onClick={() => navigate('/inactive')}
                  className="btn-red"
                  data-testid="nav-inactive-btn"
                >
                  Inactive Managers
                </Button>
              </>
            )}
            
            {/* Show Users and Add User for admin and OM */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'om') && (
              <>
                <Button
                  onClick={() => navigate('/users')}
                  className={isActive('/users') ? 'btn-gold' : 'btn-gold-outline'}
                  data-testid="nav-users-btn"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Users
                </Button>
                <Button
                  onClick={() => navigate('/add-user')}
                  className={isActive('/add-user') ? 'btn-gold' : 'btn-gold-outline'}
                  data-testid="nav-add-user-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          {/* Dashboard route - blocked for OM */}
          <Route 
            path="/" 
            element={
              currentUser?.role === 'om' ? (
                <Navigate to="/users" replace />
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                      <ClientsList user={user} onStatsUpdate={fetchStats} />
                    </div>
                    <div>
                      <RecentActivity />
                    </div>
                  </div>
                </div>
              )
            }
          />
          
          {/* Manager routes - blocked for OM */}
          <Route 
            path="/client/:clientId" 
            element={
              currentUser?.role === 'om' ? (
                <Navigate to="/users" replace />
              ) : (
                <ClientDetail user={user} onStatsUpdate={fetchStats} />
              )
            } 
          />
          <Route 
            path="/add-client" 
            element={
              currentUser?.role === 'om' ? (
                <Navigate to="/users" replace />
              ) : (
                <AddClient onStatsUpdate={fetchStats} />
              )
            } 
          />
          <Route 
            path="/inactive" 
            element={
              currentUser?.role === 'om' ? (
                <Navigate to="/users" replace />
              ) : (
                <div className="space-y-6">
                  <InactiveClientsList user={user} />
                </div>
              )
            }
          />
          
          {/* User management routes - accessible to admin and OM */}
          <Route path="/users" element={<UsersList currentUser={user} />} />
          <Route path="/add-user" element={<AddUser user={currentUser} />} />
          
          {/* Profile route - accessible to all */}
          <Route path="/profile" element={<MyProfile user={user} />} />
        </Routes>
      </main>
    </div>
  );
};

export default Dashboard;